import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles, faqList } from '@/mocks/data';

function FAQItem({ question, answer, defaultOpen = false }: { question: string; answer: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-5 text-left bg-background-50 hover:bg-background-100 transition-colors"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-foreground-900 pr-4">{question}</span>
        <i
          className={`text-foreground-500 text-lg transition-transform duration-300 flex-shrink-0 ${
            open ? 'ri-subtract-line' : 'ri-add-line'
          }`}
        ></i>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '禁漫天堂',
    url: 'https://jmtt.example.com',
    description: '禁漫天堂是深受用户喜爱的漫画阅读平台，提供最新访问入口、注册教程、内容分类导览及常见问题解答。',
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '禁漫天堂',
    url: 'https://jmtt.example.com',
    description: '禁漫天堂 - 专业漫画阅读平台',
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Japanese%20manga%20style%20artistic%20background%20with%20warm%20crimson%20and%20amber%20tones%2C%20abstract%20comic%20panel%20patterns%2C%20soft%20gradient%20atmosphere%2C%20clean%20minimalist%20composition%2C%20empty%20center%20area%20for%20text%2C%20warm%20lighting%2C%20aesthetic%20illustration%20style%2C%20no%20characters&width=1800&height=900&seq=hero-bg-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-500/20 text-accent-200 border border-accent-500/30 mb-5">
              2026 最新官方入口
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 font-heading leading-tight">
              禁漫天堂
            </h1>
            <p className="text-sm md:text-base text-background-200 max-w-2xl mb-8 leading-relaxed">
              禁漫天堂是深受用户喜爱的漫画阅读平台，提供海量高清漫画资源、
              多种阅读模式与离线下载功能，助您畅享极致漫画阅读体验
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/category/rexue"
                className="px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20 transition-colors"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                为什么选择禁漫天堂
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                六大核心优势，打造无与伦比的漫画阅读体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: 'ri-database-2-line', title: '海量资源', desc: '收录数万部精品漫画，涵盖八大热门分类，每日持续更新最新章节' },
                { icon: 'ri-speed-up-line', title: '极速加载', desc: '全球CDN加速节点部署，图片秒加载，翻页零等待' },
                { icon: 'ri-smartphone-line', title: '多端同步', desc: '支持Web、iOS、Android多平台，阅读进度自动跨设备同步' },
                { icon: 'ri-download-cloud-2-line', title: '离线下载', desc: '支持章节离线缓存，无网络环境下也能畅快阅读' },
                { icon: 'ri-contrast-2-line', title: '智能适配', desc: '多种翻页模式与画质选项，根据设备和网络智能调节' },
                { icon: 'ri-shield-check-line', title: '安全可靠', desc: '多重安全防护机制，保障账号安全和隐私数据' },
              ].map((feature, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-colors group">
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                内容分类导览
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                八大分类，海量漫画，总有一款适合你的口味
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300/50 hover:bg-background-50 transition-all text-center"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                    <i className={`${cat.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed hidden sm:block line-clamp-2">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/rexue"
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
              >
                浏览全部分类
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Latest Articles Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                  最新使用指南
                </h2>
                <p className="text-sm text-foreground-500">了解禁漫天堂的最新功能和教程</p>
              </div>
              <Link
                to="/article/1"
                className="hidden sm:inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(0, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block p-5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300/50 transition-all"
                >
                  <div className="flex items-center gap-2 mb-3">
                    {article.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 text-xs rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                常见问题解答
              </h2>
              <p className="text-sm text-foreground-500">
                快速找到您想了解的问题答案
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {faqList.map((faq, idx) => (
                <FAQItem key={idx} question={faq.question} answer={faq.answer} defaultOpen={idx === 0} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative w-full px-4 md:px-6 py-16 md:py-24 bg-background-950 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20texture%20with%20crimson%20and%20dark%20amber%20flowing%20shapes%2C%20soft%20organic%20patterns%2C%20minimalist%20dark%20background%2C%20subtle%20light%20streaks%2C%20artistic%20composition%2C%20elegant%20atmosphere&width=1800&height=600&seq=cta-bg-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-3">
              准备好开启您的漫画之旅了吗？
            </h2>
            <p className="text-sm text-background-300 mb-8 leading-relaxed">
              立即访问禁漫天堂，探索海量高清漫画，享受极致阅读体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/rexue"
                className="px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                立即浏览漫画
              </Link>
              <Link
                to="/article/2"
                className="px-6 py-2.5 text-sm font-medium whitespace-nowrap rounded-md bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20 transition-colors"
              >
                查看注册教程
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}